import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createAccount, getAccountByMongoOrganisationId } from '../api';
import type { CreateAccountInput } from '../types';

const ACCOUNT_POLL_INTERVAL_MS = 500;
const ACCOUNT_POLL_ATTEMPTS = 20;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: CreateAccountInput) => {
      const result = await createAccount(data);

      if (result.status === 'created') {
        return { id: result.id };
      }

      for (let attempt = 0; attempt < ACCOUNT_POLL_ATTEMPTS; attempt += 1) {
        await sleep(ACCOUNT_POLL_INTERVAL_MS);

        const pendingResult = await getAccountByMongoOrganisationId(
          result.mongoOrganisationId,
          result.pollToken
        );
        if (pendingResult.status === 'created') {
          return { id: pendingResult.id };
        }
      }

      throw new Error(result.message || 'Account creation is still processing. Please refresh shortly.');
    },
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account created successfully');
      navigate(`/accounts/${account.id}`);
    },
    onError: (error: Error) => {
      toast.error('Failed to create account', {
        description: error.message,
      });
    },
  });
}
